<script lang="ts" setup>
// 坂井TODO：名前がよくないので、unorderedItemsTableに変更する（カレンダーも似たような名前に）
import OrderPageTab from '@/corePagesModule/components/OrderPageTab.vue';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { i18n } from '@/i18n';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { useOrderTableStore } from './useOrderTableStore';
import NewBaseTableContent from '@/corePagesModule/BaseTableContent/NewBaseTableContent.vue';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { openLoadingModal, openOkInformationModal } from '@/corePagesModule/components/modal/modalActions';
import OrderStatusReviseModal from '@/corePagesModule/components/OrderStatusReviseModal.vue';
import AttentionOrderDeleteModal from '../components/AttentionOrderDeleteModal.vue';
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import SvgLoader from '@/utils/SvgLoader.vue';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import { TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import { filterableOrderStatus } from '@/corePagesModule/BaseTableContent/components/order/orderStatus';
import OrderStatusVisibleButton from '../components/OrderStatusVisibleButton.vue';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import NormalOrderDeleteModal from '../components/NormalOrderDeleteModal.vue';
import RegisterOrderModal from '../components/registerOrderModal/RegisterOrderModal.vue';
import RegisterOrderBusinessExcelMenuDropdown from '../components/RegisterOrderBusinessExcelMenuDropdown.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import ExportOrderModal from '../components/exportModal/ExportOrderModal.vue';
import ReminderMailModal from '../components/ReminderMailModal.vue';
import { SaveReminderMailSettingsUseCase, ReminderMailData } from '../useCases/saveReminderMailSettingsUseCase';
import { isLoading } from '@/coreDomainsModule/uiLoadingState';
import { diContainer } from '../../coreContainer/container';
import { deleteReminder } from '@/businessRepository/reminderTable/driver';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import OrderGroupMenuDropdown from '../components/orderGroup/OrderGroupMenuDropdown.vue';
import OrderGroupEditModal from '../components/orderGroup/OrderGroupEditModal.vue';
import OrderGroupAddOrderModal from '../components/orderGroup/OrderGroupAddOrderModal.vue';
import { BULK_EDIT_ORDER_MENU_ITEMS, OrderGroupModalState } from '../constants';
import OrderGroupSelectModal from '../components/orderGroup/OrderGroupSelectModal.vue';
import { OrderGroupFactory } from '@/coreDomainsModule/factories/OrderGroupFactory';
import OrderGroupSelectInput from '../components/orderGroup/OrderGroupSelectInput.vue';
import OrderBasisInfoModal from '../components/OrderBasisInfoModal.vue';
import { simpleCopy } from '@/utils/deepCopy';
import { initPagingOptions } from '@/coreDomainsModule/paging';
import DropdownButton from '@/corePagesModule/components/DropdownButton.vue';
import ChangeEtaModal from '../components/ChangeEtaModal.vue';

const unorderedArrivalStore = useOrderTableStore();
const { state } = storeToRefs(unorderedArrivalStore);
// store内で定義されているstateを引っ張ってくる
const t = i18n.t;

const {
  onChangePaging,
  sortTableContentItems,
  fetchItems,
  $reset,
  updateEditOrder,
  updateOrderStatus,
  reloadTable,
  toggleStatusFilterIcon,
  onAppliedFilterModal,
  isNeedAttentionForDelete,
  deleteOrders,
  bulkRegisterOrderExcel,
  exportOrder,
  modifyOrder,
  modifyPartialDelivery,
  deletePartialDelivery,
  addPartialDelivery,
  updateMemo,
  deleteMemo,
  onNextDeleteOrderGroup,
  onNextOrderToOrderGroupUnlinking,
  getFilterItemsValue,
  bulkUpdateOrderEta,
} = unorderedArrivalStore;

const isRefreshTable = ref(false);
const reminderMailModalState = ref(false);
const orderBasisModalState = ref(false);
const selectedOrderId = ref('');
const reminderInitialMessage = ref('');
const reminderInitialDate = ref<string | null>(null);
const reminderInitialRecipients = ref<string[]>([]);
const bulkEtaChangeModalState = ref(false);

onMounted(async () => {
  await init();
});

/**
 * 初期化処理
 */
const init = async () => {
  $reset();
  fetchItems();
  state.value.renderingKey += 1;
};

/**
 * 編集モード中かどうか
 * @returns 編集モード中かどうか
 */
const isEditing = computed(() => {
  return state.value.editingOrderId !== '';
});

// 選択されたtableContentItems
const selectedTableContentItems = computed(() => {
  return state.value.tableContentItems.filter((item) => state.value.checkedIds.includes(item.displayId));
});

// TODO: 坂井 時短でstoreに書いていないだけで、最後にstoreに書く

/**
 * テーブル変更確認モーダルで保存ボタン押下時の処理
 */
const saveOrderTableChange = async () => {
  await _saveChangeEditableCellHistoryModal();
  return;
};

/**
 * テーブル変更確認モーダルで保存せず終了を押下時の処理
 */
const revertOrderTableChange = () => {
  // 再フェッチします
  const beforePagingOption = simpleCopy(state.value.pagingOptions ?? initPagingOptions(30));
  $reset();
  state.value.pagingOptions = beforePagingOption;
  fetchItems();
  state.value.forcePagingNumber = 1 + beforePagingOption.offset / beforePagingOption.pageSize;
  state.value.editingOrderId = '';
  closeChangeConfirmModal();
};

/**
 * テーブル変更確認モーダルを閉じる処理
 */
const closeChangeConfirmModal = () => {
  state.value.changeConfirmModalState = false;
};

/**
 * 編集モード終了モーダルを表示する
 */
const _saveChangeEditableCellHistoryModal = async () => {
  closeChangeConfirmModal();
  await updateEditOrder();
};

/**
 * 発注ステータスを一括更新処理
 * @param orderStatus - 発注ステータス
 */
const bulkReviseOrderStatus = async (orderStatus: OrderStatus) => {
  state.value.orderReviseModalState = false;
  const updateResult = await openLoadingModal<'success' | 'error'>(
    t('保存中・・・'),
    async () => {
      try {
        await updateOrderStatus(orderStatus);
        return 'success';
      } catch (error) {
        console.error('error', error);
        return 'error';
      }
    },
    []
  );

  if (updateResult === 'success') {
    await showInfoModal({
      modalTitle: t('保存完了'),
      modalDescription: t('正常に保存されました'),
      nextButtonLabel: t('OK'),
      icon: 'info',
      enableOutsideClick: false,
    });
  } else {
    await showInfoModal({
      modalTitle: t('エラー'),
      modalDescription: t(
        '何らかの理由で保存に失敗しました。再度時間を置いて実行していただくか、それでも解決しない場合はお問い合わせください。'
      ),
      nextButtonLabel: t('OK'),
      icon: 'error',
      enableOutsideClick: false,
    });
  }

  init();
  state.value.editingOrderId = '';
  isRefreshTable.value = true;
  isRefreshTable.value = false;
};

/**
 * 発注削除モーダルを表示する
 */
const openOrderDeleteModal = () => {
  state.value.orderDeleteModalState = true;
};

/**
 * 発注取り込みモーダルを表示する
 */
const openRegisterOrderModal = () => {
  state.value.registerOrderModalState = true;
};

/**
 * 発注出力モーダルを表示する
 */
const openExportOrderModal = () => {
  state.value.exportOrderModalState = true;
};

/**
 * 発注を一括削除
 * @param cancelReason - キャンセル理由
 */
const bulkDeleteOrder = async (cancelReason?: string) => {
  // まず、モーダルを閉じる
  state.value.orderDeleteModalState = false;

  const deleteResult = await openLoadingModal<'success' | 'error'>(
    t('削除中・・・'),
    async () => {
      try {
        await deleteOrders(state.value.checkedIds);
        return 'success';
      } catch (error) {
        console.error('error', error);
        return 'error';
      }
    },
    []
  );

  if (deleteResult === 'error') {
    await showInfoModal({
      modalTitle: t('エラー'),
      modalDescription: t('削除処理に失敗しました。時間を置いて再度実行してください。'),
      nextButtonLabel: t('OK'),
      icon: 'error',
      enableOutsideClick: false,
    });
  }

  init();
  fetchItems();
  state.value.editingOrderId = '';
  isRefreshTable.value = true;
  isRefreshTable.value = false;
};

/**
 * 発注エクセル登録モーダルを閉じた時の処理
 * @param file - 登録ファイル
 */
const onClosedRegisterOrderExcelModal = async (file?: File) => {
  if (!file) {
    openOkInformationModal('error', t('エラー'), 'OK', t('ファイルの読み取りに失敗しました'));
    return;
  }
  // 発注エクセル登録モーダルを閉じる
  state.value.registerOrderModalState = false;

  await bulkRegisterOrderExcel(file);

  // 初期化処理
  init();
};

/**
 * 発注出力モーダルで出力ボタンを押した際の処理
 * @param file - 選択されたファイル
 */
// const onExportOrderModalExportButtonClicked = async () => {
//   const isSuccess = await exportOrder();
const onExportOrderModalExportButtonClicked = async (file?: File) => {
  const isSuccess = await exportOrder(file);
  if (isSuccess) {
    init();
  }
  state.value.checkedIds = [];
};

/**
 * テーブルの変更ボタンを押した際の処理
 * @param id - ID
 */
const onClickEditButton = (id: string) => {
  state.value.editingOrderId = id;
  state.value.orderGroupState.originalOrderAmounts = state.value.items.reduce<Record<string, number>>((acc, item) => {
    acc[item.orderId] = item.orderAmount;
    return acc;
  }, {});
};

const isOrderGroupSelectModalOpening = computed(
  () =>
    state.value.orderGroupState.openingModal === OrderGroupModalState.OrderGroupEdit ||
    state.value.orderGroupState.openingModal === OrderGroupModalState.OrderGroupDelete
);

const getOrderGroupModalContent = computed(
  (): {
    title: string;
    description: string;
    nextButtonLabel: string;
    onNext: () => void;
  } =>
    state.value.orderGroupState.openingModal === OrderGroupModalState.OrderGroupDelete
      ? {
          title: t('削除する発注グループを選択'),
          description: t('選択した発注グループを削除します。'),
          nextButtonLabel: t('削除'),
          onNext: onNextDeleteOrderGroup,
        }
      : {
          title: t('編集する発注グループを選択'),
          description: t('選択した発注グループの取引を編集できます。'),
          nextButtonLabel: t('決定'),
          // eslint-disable-next-line jsdoc/require-jsdoc
          onNext: () => {
            state.value.orderGroupState.openingModal = OrderGroupModalState.None;
            state.value.orderGroupState.isOrderGroupEditMode = true;
          },
        }
);

watch(
  () => state.value.orderGroupState.isOrderGroupEditMode,
  async (val) => {
    if (val) {
      const targetGroupName = state.value.orderGroupState.targetGroupName;
      $reset();
      state.value.orderGroupState.isOrderGroupEditMode = true;
      state.value.orderGroupState.targetGroupName = targetGroupName;
      await fetchItems();
    }
  }
);

/**
 * 発注グループ編集モードから元の画面に戻った時の処理
 */
const onBackOrderGroupEditMode = async () => {
  state.value.orderGroupState.isOrderGroupEditMode = false;
  state.value.orderGroupState.targetGroupName = '';
  state.value.orderGroupState.editingGroup = OrderGroupFactory.create();
  await fetchItems();
};

/**
 * リマインダーメール設定モーダルを表示する
 * @param orderId - 発注ID
 */
const openReminderMailModal = (orderId: string) => {
  selectedOrderId.value = orderId;
  // 既存のリマインダーデータをセット（存在する場合）
  const orderItem = state.value.tableContentItems.find((item) => item.orderId === orderId);
  const reminderId = orderItem?.reminderMailDataId ?? null;
  const existing = reminderId ? state.value.reminderData.find((r) => r.dataId === reminderId) : null;
  // メモ欄にはメール本文ではなく「発注メモ」を初期表示する
  reminderInitialMessage.value = orderItem?.orderNote ?? '';
  reminderInitialDate.value = existing?.date ?? null;
  reminderInitialRecipients.value = existing?.mailTo ?? [];
  reminderMailModalState.value = true;
};

/**
 * リマインダーメール設定モーダルを閉じる
 */
const closeReminderMailModal = () => {
  reminderMailModalState.value = false;
  selectedOrderId.value = '';
  reminderInitialMessage.value = '';
  reminderInitialDate.value = null;
  reminderInitialRecipients.value = [];
};

/**
 * リマインダーメール設定を保存する
 * @param reminderMailData - リマインダーメールデータ
 */
const saveReminderMailSettings = async (reminderMailData: ReminderMailData) => {
  try {
    // 一連の処理が終わるまでテーブルはローディング表示にする
    state.value.loadingState = { status: { type: 'loading' } };
    // リマインダーメール設定を保存
    const saveReminderMailSettingsUseCase = new SaveReminderMailSettingsUseCase();
    const reminderResult = await saveReminderMailSettingsUseCase.execute(reminderMailData);

    if (reminderResult.success && reminderResult.dataId) {
      // 成功時：発注データのreminderMailDataIdを更新
      const orderId = reminderMailData.orderId;
      const orderItem = state.value.tableContentItems.find((item) => item.orderId === orderId);

      if (orderItem) {
        const updateResult = await diContainer.updateOrderReminderMailIdUseCase.Execute(
          orderId,
          reminderResult.dataId,
          orderItem.updatedAt,
          reminderMailData.message
        );

        if (updateResult.status.type === 'success') {
          // バックエンド更新でupdatedAtが更新されるため、最新を再取得
          await reloadTable();

          await showInfoModal({
            modalTitle: t('保存完了'),
            modalDescription: t('リマインドメールの設定に成功しました。'),
            nextButtonLabel: t('OK'),
            icon: 'info',
            enableOutsideClick: false,
          });
        } else {
          console.error('Failed to update order reminderMailDataId:', updateResult.status.error);
          await showInfoModal({
            modalTitle: t('エラー'),
            modalDescription: t('発注データの更新に失敗しました'),
            nextButtonLabel: t('OK'),
            icon: 'error',
            enableOutsideClick: false,
          });
        }
      }
    } else {
      console.error('Failed to save reminder mail settings:', reminderResult);
      await showInfoModal({
        modalTitle: t('エラー'),
        modalDescription: t('リマインドメールの設定に失敗しました。'),
        nextButtonLabel: t('OK'),
        icon: 'error',
        enableOutsideClick: false,
      });
    }
  } catch (error) {
    console.error('Error in saveReminderMailSettings:', error);
    await showInfoModal({
      modalTitle: t('エラー'),
      modalDescription: t('予期しないエラーが発生しました'),
      nextButtonLabel: t('OK'),
      icon: 'error',
      enableOutsideClick: false,
    });
  } finally {
    // ローディング解除（fetchが走っていればそちらで成功に遷移）
    if (state.value.loadingState.status.type === 'loading') {
      state.value.loadingState = { status: { type: 'success' } };
    }
    closeReminderMailModal();
  }
};

/**
 * リマインダーメール設定を削除し、発注から紐付きを解除する
 */
const deleteReminderMailSettings = async () => {
  try {
    const orderId = selectedOrderId.value;
    if (!orderId) return;

    const orderItem = state.value.tableContentItems.find((item) => item.orderId === orderId);
    const reminderId = orderItem?.reminderMailDataId ?? null;

    if (!orderItem || !reminderId) {
      // 何もすることがなければ終了
      return;
    }

    await waitLoading(
      (async () => {
        const delRes = await deleteReminder(reminderId);
        if (!delRes.success) throw new Error('Failed to delete reminder');

        // API仕様: dataId削除時は空文字を送る
        const updateRes = await diContainer.updateOrderReminderMailIdUseCase.Execute(orderId, '', orderItem.updatedAt);
        if (updateRes.status.type !== 'success') throw new Error('Failed to clear reminderMailDataId');

        await reloadTable();
      })(),
      t('削除中です・・・'),
      ''
    );
  } catch (error) {
    console.error('Error in deleteReminderMailSettings:', error);
    await showInfoModal({
      modalTitle: t('エラー'),
      modalDescription: t('リマインド削除に失敗しました'),
      nextButtonLabel: t('OK'),
      icon: 'error',
      enableOutsideClick: false,
    });
  } finally {
    closeReminderMailModal();
  }
};

/**
 * 発注根拠モーダルを開く
 * @param orderId - 発注ID
 */
const openOrderBasisInfoModal = async (orderId: string) => {
  selectedOrderId.value = orderId;
  const orderItem = state.value.tableContentItems.find((item) => item.orderId === orderId);
  const orderBasisId = orderItem?.orderBasisId ?? undefined;
  if (!orderBasisId) {
    // 紐づいた発注根拠がないのでエラーを出す
    await showInfoModal({
      modalTitle: t('エラーが発生しました。'),
      modalDescription: t('発注根拠が存在しません。'),
      isDisabledNextButton: true,
      backButtonLabel: t('OK'),
      colorStyle: 'error',
      icon: 'error',
    });
    return;
  }

  orderBasisModalState.value = true;
};

/**
 * 発注根拠モーダルを閉じる
 */
const closeOrderBasisInfoModal = () => {
  selectedOrderId.value = '';
  orderBasisModalState.value = false;
};

/**
 * 一括編集メニューボタンを押した時の処理
 * @param value - 選択した値
 */
const onClickBulkEditButton = (value: string) => {
  if (value === 'eta') {
    if (!checkEditableOrder()) {
      showInfoModal({
        modalTitle: t('納期を変更できない発注が含まれています'),
        modalDescription: t(
          '数量回答待ち/分納/確定/納入済みのステータスの発注は一括で納期を変更することができません。先にステータスを変更してください。'
        ),
        backButtonLabel: t('OK'),
        isDisabledNextButton: true,
      });
    } else bulkEtaChangeModalState.value = true;
  } else if (value === 'status') {
    state.value.orderReviseModalState = true;
  }
};

/**
 * ETAを一括編集する発注が条件に合致しているかチェックする
 */
const checkEditableOrder = (): boolean => {
  const invalidOrderStatus = [
    OrderStatus.Confirmed,
    OrderStatus.Delivered,
    OrderStatus.DeliveryDateConfirmed,
    OrderStatus.DisplayEnded,
    OrderStatus.PartialDelivery,
  ] as OrderStatus[];
  return !state.value.items
    .filter((order) => state.value.checkedIds.includes(order.orderId))
    .some((order) => invalidOrderStatus.includes(order.orderStatus));
};

/**
 * ETA一括編集モーダルのonNext
 * @param eta - 変更するETA
 */
const onNextBulkEtaEditModal = async (eta: number | null) => {
  if (!eta) return;
  bulkEtaChangeModalState.value = false;
  bulkUpdateOrderEta(eta);
};
</script>

<template>
  <!-- 発注テーブルーページ -->
  <div class="unordered-items-table">
    <!-- タイトル -->
    <div class="unordered-items-table__title unordered-table-title">
      <span class="unordered-table-title__label">{{ t('発注') }}</span>
      <div v-if="!isEditing" class="unordered-table-title__not-export-order not-export-order">
        <RouterLink class="not-export-order__label" to="/not-exported-order">{{ t('未出力の発注ページに切り替え') }}</RouterLink>
        <SvgLoader class="not-export-order__icon" name="orderSwitchIcon" />
      </div>
    </div>

    <div class="unordered-items-table__header unordered-table-header">
      <!-- ページ遷移タブ -->
      <OrderPageTab class="" />

      <div
        v-if="!isEditing && !state.orderGroupState.isOrderGroupEditMode"
        class="unordered-table-header__bulk-action-button-container bulk-action-button-container"
      >
        <DropdownButton :options="BULK_EDIT_ORDER_MENU_ITEMS" :disabled="!state.checkedIds.length" @click-option="onClickBulkEditButton">
          <template #label>
            {{ t('発注一括編集') }}
          </template>
        </DropdownButton>
        <TextButton
          :button-style="'btn-secondary'"
          :size="'btn-md'"
          :on-click="openOrderDeleteModal"
          :disabled="state.checkedIds.length === 0"
          class="bulk-action-button-container__button"
        >
          {{ t('発注削除') }}
        </TextButton>
        <TextButton class="bulk-action-button-container__button" @click="openRegisterOrderModal()">
          {{ t('発注情報の取込') }}
        </TextButton>
        <TextButton
          :button-style="'btn-primary'"
          :size="'btn-md'"
          :on-click="openExportOrderModal"
          :disabled="state.checkedIds.length === 0"
          class="bulk-action-button-container__button bulk-action-button-container__export-button"
        >
          {{ t('発注出力') }}
          <SvgLoader name="download" />
        </TextButton>
        <div class="bulk-action-button-container__filter-order-status">
          <template v-for="(status, index) in filterableOrderStatus" :key="index">
            <OrderStatusVisibleButton
              class="order-status__container--badge"
              :order-status-str="status"
              :is-show-order-status="state.orderStatusFilterItem[status]"
              @click="toggleStatusFilterIcon(status, $event)"
            />
          </template>
        </div>
      </div>
    </div>
    <!-- フィルタボタン -->
    <div class="unordered-items-table__contains">
      <div v-if="!isEditing" class="unordered-items-table__head-area">
        <div class="unordered-items-table__head-area--left">
          <!-- フィルターモーダル -->
          <FilterModalButton
            class="detail-filter-modal"
            :filter-modal-type="'order'"
            :select-input-index="0"
            :filter-items="getFilterItemsValue().value"
            @apply="(filteredContents) => onAppliedFilterModal(filteredContents)"
          />
          <template v-if="state.orderGroupState.isOrderGroupEditMode">
            <OrderGroupSelectInput label="" @change="async () => await fetchItems()" />
            <TextButton
              button-style="btn-secondary"
              @click="
                () => {
                  state.orderGroupState.openingModal = OrderGroupModalState.OrderGroupInfoEdit;
                }
              "
            >
              {{ t('発注グループ情報を編集') }}
            </TextButton>
            <TextButton button-style="btn-secondary" :disabled="!state.checkedIds.length" @click="onNextOrderToOrderGroupUnlinking">
              {{ t('発注グループから外す') }}
            </TextButton>
            <TextButton button-style="btn-secondary" @click="onBackOrderGroupEditMode">
              {{ t('元のページに戻る') }}
            </TextButton>
          </template>
          <OrderGroupMenuDropdown v-else />
        </div>
        <Pagination
          v-if="state.pagingOptions"
          :paging-options="state.pagingOptions"
          :is-loading="state.loadingState.status.type === 'loading'"
          is-order-page
          :set-page-number="state.forcePagingNumber"
          class="pagination"
          @on-change-page="onChangePaging($event)"
        />
      </div>
      <NewBaseTableContent
        :key="state.renderingKey"
        :prop-table-content-properties="state.tableContentProperties"
        :prop-display-objects="state.tableContentItems"
        :prop-loading-status="state.loadingState"
        :prop-table-type="'firmOrderTable'"
        :prop-is-reset="isRefreshTable"
        :prop-editing-id="state.editingOrderId"
        :prop-is-editing="state.orderGroupState.isOrderGroupEditMode"
        class="unordered-items-table__contains unordered-items-table__contains--table"
        @checked="state.checkedIds = $event"
        @on-click-sort="(sortKey: TableContentProperty, isAsc: boolean) => sortTableContentItems(sortKey, isAsc)"
        @reload="reloadTable"
        @on-click-edit="onClickEditButton($event)"
        @on-click-save="state.changeConfirmModalState = true"
        @on-change-order-property="(orderId, key, value) => modifyOrder(orderId, key, value)"
        @on-change-partial-delivery="
          (orderId, partialDeliveryId, key, value) => modifyPartialDelivery(orderId, partialDeliveryId, key, value)
        "
        @on-click-delete="(orderId, partialDeliveryId) => deletePartialDelivery(orderId, partialDeliveryId)"
        @on-click-add-partial-delivery="(orderId) => addPartialDelivery(orderId)"
        @on-click-save-memo="(orderId, memo) => updateMemo(orderId, memo)"
        @on-click-delete-memo="(orderId) => deleteMemo(orderId)"
        @on-click-reminder-mail="openReminderMailModal"
        @on-click-order-basis="openOrderBasisInfoModal"
      />
    </div>
    <OrderStatusReviseModal
      :modal-state="state.orderReviseModalState"
      :items="selectedTableContentItems"
      :is-bulk-edit="true"
      @canceled="() => (state.orderReviseModalState = false)"
      @confirm="bulkReviseOrderStatus($event as OrderStatus)"
    />

    <template v-if="state.orderDeleteModalState">
      <AttentionOrderDeleteModal
        v-if="isNeedAttentionForDelete()"
        :modal-state="state.orderDeleteModalState"
        @canceled="() => (state.orderDeleteModalState = false)"
        @confirm="bulkDeleteOrder($event as OrderStatus)"
      />
      <NormalOrderDeleteModal
        v-else
        :modal-state="state.orderDeleteModalState"
        @canceled="() => (state.orderDeleteModalState = false)"
        @confirm="bulkDeleteOrder()"
      />
    </template>
  </div>
  <!-- ダウンロードモーダル -->
  <RegisterOrderModal
    :modal-state="state.registerOrderModalState"
    @close-modal="() => (state.registerOrderModalState = false)"
    @export-order="onClosedRegisterOrderExcelModal($event)"
  />
  <!-- 編集完了時確認モーダル -->
  <InfoModal
    :modal-state="state.changeConfirmModalState"
    icon="info"
    :enable-outside-click="false"
    :back-button-label="t('キャンセル')"
    :next-button-label="t('保存する')"
    @on-next="saveOrderTableChange"
    @on-back="closeChangeConfirmModal"
  >
    <template #title>{{ t('編集モードの終了') }}</template>
    <template #description>{{ t('ここまでの編集及び変更点を保存しますか') }}</template>
    <template #footer>
      <div class="confirm-edit-modal__footer" @click="revertOrderTableChange">{{ t('保存せずに終了(発注画面)') }}</div>
    </template>
  </InfoModal>
  <!-- 発注出力モーダル -->
  <!-- NOTE: FileInputを使用しない(S3テンプレート)を使用する場合 -->
  <!-- <ExportOrderModal
    :modal-state="state.exportOrderModalState"
    @close-modal="state.exportOrderModalState = false"
    @export-order="onExportOrderModalExportButtonClicked()"
  /> -->
  <ExportOrderModal
    :modal-state="state.exportOrderModalState"
    @close-modal="state.exportOrderModalState = false"
    @export-order="onExportOrderModalExportButtonClicked($event)"
  />
  <!-- メモ更新モーダル -->
  <InfoModal :modal-state="isLoading(state.updateMemoState.loading.status)" icon="loading" :enable-outside-click="false">
    <template #title>{{ state.updateMemoState.workType === 'delete' ? t('メモを削除しています') : t('メモを保存しています') }}</template>
  </InfoModal>
  <!-- リマインダーメール設定モーダル -->
  <ReminderMailModal
    :modal-state="reminderMailModalState"
    :order-id="selectedOrderId"
    :initial-message="reminderInitialMessage"
    :initial-reminder-date="reminderInitialDate"
    :initial-recipients="reminderInitialRecipients"
    @close-modal="closeReminderMailModal"
    @save-reminder-mail="saveReminderMailSettings"
    @delete-reminder-mail="deleteReminderMailSettings"
  />

  <!-- 発注グループモーダル -->
  <OrderGroupEditModal />
  <OrderGroupAddOrderModal />
  <OrderGroupSelectModal
    :is-opening="isOrderGroupSelectModalOpening"
    :title="getOrderGroupModalContent.title"
    :description="getOrderGroupModalContent.description"
    :next-button-label="getOrderGroupModalContent.nextButtonLabel"
    @next="getOrderGroupModalContent.onNext"
    @back="
      () => {
        state.orderGroupState.openingModal = OrderGroupModalState.None;
        state.orderGroupState.targetGroupName = '';
      }
    "
  />
  <OrderBasisInfoModal
    :modal-state="orderBasisModalState"
    :order-data="state.tableContentItems.find((item) => item.orderId === selectedOrderId)"
    @close="closeOrderBasisInfoModal"
  />
  <ChangeEtaModal
    :modal-state="bulkEtaChangeModalState"
    :selected-count="state.checkedIds.length"
    @close="bulkEtaChangeModalState = false"
    @next="onNextBulkEtaEditModal"
  />
</template>

<style lang="scss" scoped>
.unordered-items-table {
  display: grid;
  gap: 16px;
  margin: 16px;

  &__title {
    font-family: Noto Sans JP;
    font-size: 22px;
    font-weight: 700;
    line-height: 32px;
    letter-spacing: 0em;
    text-align: left;
    color: $textDefault;
  }

  &__contains {
    display: grid;
    gap: 8px;
    &--loading {
      align-self: center;
      height: 100%;
    }
  }

  &__head-area {
    display: grid;
    grid-auto-flow: column;
    justify-content: space-between;
    &--left {
      display: grid;
      grid-auto-flow: column;
      align-items: center;
      gap: 16px;
    }
  }
}

.unordered-table-title {
  display: grid;
  grid-template-columns: repeat(2, fit-content(100%));
  align-items: center;
  column-gap: 48px;
  &__label {
    font-size: 22px;
    font-weight: 700;
    line-height: 28px;
  }
}

.not-export-order {
  display: grid;
  height: 100%;
  grid-template-columns: auto auto;
  align-items: baseline;
  justify-items: center;
  column-gap: 4px;
  pointer-events: none;
  cursor: pointer;
  padding: 0px 12px 4px 12px;

  &:hover {
    background: $semanticPrimarySubdued;
    transition: 0.3ms;
    border-radius: 90px;
  }
  &__label {
    pointer-events: auto;
    font-size: 16px;
    font-weight: 700;
    line-height: 16px;
    color: $semanticPrimary;
  }
  &__icon {
    pointer-events: auto;
    width: 20px;
    height: 20px;
    align-content: end;
  }
}

.unordered-table-header {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 32px;
  &__page-tab {
    align-items: end;
  }
  &__bulk-action-button-container {
    align-self: center;
  }
}

.bulk-action-button-container {
  display: grid;
  grid-template-columns: repeat(5, fit-content(100%)) 1fr;
  align-items: center;
  width: 100%;
  &__button {
    margin-left: 16px;
    font-size: 14px;
    right: 0px;
  }
  &__export-button {
    display: grid;
    grid-template-columns: auto auto;
    gap: 2px;
    padding: 8px 23px;
    align-items: center;
  }
  &__cancel-button {
    right: 0px;
    font-size: 14px;
  }
  &__filter-order-status {
    display: grid;
    grid-auto-flow: column;
    gap: 8px;
    margin-left: 32px;
  }
  &__edit-button {
    justify-self: end;
    margin-left: 16px;
    height: 24px;
    right: 0px;
  }
}

.dli-loading-1 {
  display: inline-block;
  vertical-align: middle;
  color: #666;
  line-height: 1;
  width: 4em;
  height: 4em;
  border: 0.12em solid currentColor;
  border-top-color: rgba(102, 102, 102, 0.3);
  border-radius: 50%;
  box-sizing: border-box;
  animation: rotate 1s linear infinite;
}

.select-filter-button {
  width: 132px;
  height: 32px;
  padding: 0 12px;
  background-color: none;
  display: grid;
  place-content: center;
  line-height: 24px;

  &__container {
    display: grid;
    grid-template-columns: auto 24px;
    place-items: center;
    gap: 4px;
  }

  &__text {
    font-size: 16px;
    line-height: 24px;
    font-weight: 700;
    color: $semanticPrimary;
  }
}

.confirm-edit-modal {
  &__footer {
    font-size: 12px;
    font-weight: 500;
    text-align: center;
    color: $semanticPrimary;
    border-bottom: 1px solid $semanticPrimary;
    width: fit-content;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }
}

@keyframes rotate {
  0% {
    transform: rotate(0);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
